import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';

import { cn } from '@april9au/react-common/utils';

import type { AnswerPayload, Prompt, PromptOption } from '@/chat/types';
import type { IconName } from '@/components/ui/icon';
import type { RTImageNode } from '@prismicio/client';
import { labelImage, labelText } from '@/chat/label';
import { Icon } from '@/components/ui/icon';
import { Media } from '@/components/ui/media';
import { formatString } from '@/i18n/catalogue';
import { useStrings } from '@/i18n/use-strings';
import { answerKey } from '@/lib/autopilot';
import { ActivityFooter } from './activities/activity-footer';
import { plainTextOr } from './rich-text';

/**
 * The one place an answer is given. Every question type in the course — quick
 * replies, single-select, multi-select, and (ticket 07) free text and the
 * activity footer — is a presentation of this component, keyed by
 * `pending.kind`. Nothing else in the chat renders answer UI.
 *
 * The `data-autopilot` attributes are inert hooks for the non-production
 * Autopilot, which taps these controls for real; `answerKey` marks the correct
 * option and is compiled out of a production build (`app/lib/autopilot.ts`).
 */

export type AnswerControlProps = {
  prompt: Prompt;
  /** Attempts on this prompt so far, 1-based. Changes the heading on a retry. */
  attempt: number;
  onAnswer: (payload: AnswerPayload) => void;
  /**
   * Opens the gating video's player. The `gate` prompt is the only one that
   * uses it: its answer is inside the player, so the footer has to be able to
   * put the player back on screen.
   */
  onOpenGate: () => void;
  /** The `reveal` event. Only the activity footer offers it. */
  onReveal: () => void;
};

/** The ✓ / ✕ on a locked answer. Over a picture tile it floats in the corner. */
function Mark({ name, floating }: { name: IconName; floating: boolean }) {
  return floating ? (
    <span className="bg-bubble-buddy absolute end-1 top-1 grid size-6 place-items-center rounded-full shadow-sm">
      <Icon name={name} className="size-4" />
    </span>
  ) : (
    <Icon name={name} className="size-4 shrink-0" />
  );
}

/** What one option shows, and what a screen reader should call it. */
type OptionParts = {
  /** The picture the option carries, or `null` when it is words alone. */
  picture: RTImageNode | null;
  /** What it says: the authored words, else the picture's alt text. */
  words: string;
  /** Its accessible name — `words`, or its position when it has none. */
  name: string;
};

/**
 * The one place an option is read, so `single` and `multi` cannot disagree
 * about it.
 *
 * `labelText` prefers the authored words and falls back to the picture's alt
 * text. Half of the repository's picture assets carry neither, so the last
 * resort is the option's position: every one of those questions says "tap
 * the ...", which makes "Option 2" the honest name rather than a guess.
 */
function optionParts(option: PromptOption, index: number, numbered: string): OptionParts {
  const words = labelText(option.label);

  return {
    picture: labelImage(option.label),
    words,
    name: words === '' ? formatString(numbered, { number: index + 1 }) : words,
  };
}

/**
 * A picture answer, sized from the asset so the answer bar never grows under
 * the learner's thumb. White behind it: the flags and signs are cut-outs on
 * transparency, and a tinted plate turns a yellow flag olive.
 */
function OptionPicture({ node, className }: { node: RTImageNode; className?: string }) {
  return (
    <Media
      src={node.url}
      alt=""
      width={node.dimensions.width}
      height={node.dimensions.height}
      placeholder="var(--color-surf-gray)"
      className={cn('rounded-lg bg-white object-contain', className)}
    />
  );
}

/** Base chip: 44 px minimum target, one named transition, tactile press. */
const CHIP =
  'chat-chip-in chat-pressable min-h-11 rounded-full border px-4 text-[15px] font-semibold focus-visible:ring-[3px] focus-visible:outline-none disabled:opacity-100';

function Heading({ children }: { children: string }) {
  return (
    <span className="text-chat-ink-muted block text-center text-[11px] font-bold tracking-[0.12em] uppercase">
      {children}
    </span>
  );
}

export function AnswerControl({ prompt, attempt, onAnswer, onOpenGate, onReveal }: AnswerControlProps) {
  const { chat } = useStrings();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [locked, setLocked] = useState<string | null>(null);
  /** The chip on its way out: it lifts away while the others drop. */
  const [sending, setSending] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  // A new prompt — or a retry of the same one — resets the control.
  useEffect(() => {
    setPicked(new Set());
    setLocked(null);
    setSending(null);
    setDraft('');
  }, [prompt, attempt]);

  const exitClass = (id: string) =>
    sending === null ? undefined : sending === id ? 'chat-chip-sent' : 'chat-chip-dropped';

  /** Lets the exit animation play out before the next turn starts. */
  const send = (id: string, payload: AnswerPayload, delay = 200) => {
    setSending(id);
    setTimeout(() => onAnswer(payload), delay);
  };

  if (prompt.kind === 'single') {
    const heading = attempt > 1 ? chat.chat_try_again : chat.chat_pick_one;
    // Most of the course's quizzes are pictures — "tap the flag that marks the
    // safest place to swim" is three photographs — so when every option is one,
    // the answer bar becomes a grid of tiles instead of blank full-width rows.
    // The tiles show the picture alone: the authored words name the sign
    // ("No alcohol sign"), so printing them under it gives the answer away.
    // They stay the button's accessible name and are echoed once tapped.
    const tiles = prompt.options.every((option) => labelImage(option.label) !== null);

    return (
      <div data-autopilot="answer" data-autopilot-kind="single" className="flex flex-col gap-2">
        <Heading>{heading}</Heading>
        <div className={cn(tiles ? 'grid grid-cols-3 gap-2' : 'flex flex-col gap-2')}>
          {prompt.options.map((option, index) => {
            const state =
              locked === null ? 'idle' : locked === option.id ? (option.correct ? 'right' : 'wrong') : 'off';
            const { picture, words, name } = optionParts(option, index, chat.chat_option_number);

            return (
              <button
                key={option.id}
                type="button"
                data-autopilot="chip"
                {...answerKey(option.correct)}
                style={{ '--chip-index': index } as CSSProperties}
                disabled={locked !== null}
                // A picture always carries its name explicitly: a tile shows
                // no words, and an unnamed button is silent.
                aria-label={picture === null ? undefined : name}
                onClick={() => {
                  setLocked(option.id);
                  // Hold the ✓/✕ long enough to be read before the turn plays.
                  send(option.id, { kind: 'single', optionId: option.id }, option.correct ? 420 : 620);
                }}
                className={cn(
                  CHIP,
                  exitClass(option.id),
                  tiles
                    ? 'relative flex flex-col justify-center gap-1 rounded-xl p-1.5 text-center text-xs leading-tight'
                    : 'flex w-full items-center justify-between gap-3 rounded-xl px-4 py-2.5 text-start',
                  state === 'idle' && 'border-chat-hairline bg-bubble-buddy text-chat-ink hover:border-surf-blue',
                  state === 'right' && 'border-answer-correct bg-answer-correct/10 text-answer-correct',
                  state === 'wrong' && 'border-answer-wrong bg-answer-wrong/8 text-answer-wrong',
                  state === 'off' && 'border-chat-hairline bg-bubble-buddy text-chat-ink-muted',
                  'focus-visible:ring-surf-blue/40',
                )}
              >
                {picture === null ? null : (
                  <OptionPicture node={picture} className={tiles ? 'aspect-[4/3] w-full' : 'me-1 size-12 shrink-0'} />
                )}
                {tiles ? null : words}
                {state === 'right' ? <Mark name="passport/check" floating={tiles} /> : null}
                {state === 'wrong' ? <Mark name="passport/close" floating={tiles} /> : null}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (prompt.kind === 'multi') {
    const chosen = prompt.options.filter((option) => picked.has(option.id));

    // Every checklist in the repository is authored as a word and a photograph
    // — "sun hat", "sunscreen" — so the picture is half the answer.
    const tiles = prompt.options.some((option) => labelImage(option.label) !== null);

    return (
      <div data-autopilot="answer" data-autopilot-kind="multi" className="flex flex-col gap-2">
        <Heading>{attempt > 1 ? chat.chat_try_again : chat.chat_pick_all}</Heading>
        <div className={cn(tiles ? 'grid grid-cols-3 gap-2' : 'flex flex-wrap justify-center gap-2')}>
          {prompt.options.map((option, index) => {
            const on = picked.has(option.id);
            const { picture, words, name } = optionParts(option, index, chat.chat_option_number);

            return (
              <button
                key={option.id}
                type="button"
                data-autopilot="chip"
                {...answerKey(option.correct)}
                style={{ '--chip-index': index } as CSSProperties}
                aria-pressed={on}
                aria-label={picture === null ? undefined : name}
                disabled={sending !== null}
                onClick={() =>
                  setPicked((current) => {
                    const next = new Set(current);

                    if (next.has(option.id)) {
                      next.delete(option.id);
                    } else {
                      next.add(option.id);
                    }

                    return next;
                  })
                }
                className={cn(
                  CHIP,
                  sending !== null && 'chat-chip-dropped',
                  'focus-visible:ring-surf-blue/40 flex items-center gap-2',
                  tiles && 'relative flex-col gap-1 rounded-xl p-1.5 text-center text-xs leading-tight',
                  on
                    ? 'border-surf-blue bg-surf-blue text-white'
                    : 'border-chat-hairline bg-bubble-buddy text-chat-ink hover:border-surf-blue',
                )}
              >
                <span
                  className={cn(
                    'grid size-4 shrink-0 place-items-center rounded-sm border',
                    tiles && 'absolute start-2 top-2 z-10',
                    on ? 'text-surf-blue border-white bg-white' : 'border-chat-hairline bg-white/90',
                  )}
                  aria-hidden
                >
                  {on ? <Icon name="passport/check" className="size-3" /> : null}
                </span>
                {picture === null ? null : <OptionPicture node={picture} className="aspect-square w-full" />}
                {words}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          data-autopilot="confirm"
          style={{ '--chip-index': prompt.options.length } as CSSProperties}
          disabled={chosen.length < prompt.min || sending !== null}
          onClick={() => send('submit', { kind: 'multi', optionIds: chosen.map((option) => option.id) }, 220)}
          className={cn(
            CHIP,
            sending === 'submit' && 'chat-chip-sent',
            'border-surf-red bg-surf-red focus-visible:ring-surf-red/40 w-full rounded-xl text-white',
            'disabled:border-chat-hairline disabled:bg-chat-rail disabled:text-chat-ink-muted',
          )}
        >
          {chosen.length > 0
            ? formatString(chat.chat_check_answer_count, { count: chosen.length })
            : chat.chat_check_answer}
        </button>
      </div>
    );
  }

  if (prompt.kind === 'discover') {
    // A discovery is a set the learner works through, so the control is a
    // shrinking list rather than a set of answers: every entry is right, the
    // one just chosen is gone, and the count says how much is left. Two
    // columns at every width — entry names are proper nouns ("Great
    // Hammerhead Shark"), which a wrapping pill breaks badly.
    const heading = plainTextOr(prompt.heading, chat.chat_discover_heading);
    const counter = formatString(chat.chat_discover_remaining, { done: prompt.done, total: prompt.total });

    return (
      <div data-autopilot="answer" data-autopilot-kind="discover" className="flex flex-col gap-2">
        <Heading>{`${heading} · ${counter}`}</Heading>
        <div className="grid grid-cols-2 gap-2">
          {prompt.options.map((option, index) => (
            <button
              key={option.id}
              type="button"
              data-autopilot="chip"
              style={{ '--chip-index': index } as CSSProperties}
              disabled={sending !== null}
              onClick={() => send(option.id, { kind: 'discover', optionId: option.id })}
              className={cn(
                CHIP,
                exitClass(option.id),
                'border-chat-hairline bg-bubble-buddy text-chat-ink focus-visible:ring-surf-blue/40 hover:border-surf-blue',
                'flex items-center justify-between gap-2 rounded-xl px-3.5 py-2.5 text-start text-[13px] leading-tight',
              )}
            >
              {labelText(option.label)}
              <Icon name="passport/chevron-right" aria-hidden className="size-4 shrink-0 opacity-50 rtl:rotate-180" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (prompt.kind === 'text') {
    // Ticket 07 (`activity` variation `free_text`). Wired, deliberately plain.
    const value = draft.slice(0, prompt.maxLength);

    return (
      <form
        data-autopilot="answer"
        data-autopilot-kind="text"
        className="flex items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();

          if (value.trim() !== '') {
            onAnswer({ kind: 'text', value: value.trim() });
          }
        }}
      >
        <input
          data-autopilot="text-input"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          maxLength={prompt.maxLength}
          placeholder={prompt.placeholder ?? chat.chat_text_placeholder}
          aria-label={chat.chat_text_placeholder}
          className="border-chat-hairline bg-bubble-buddy text-chat-ink focus-visible:ring-surf-blue/40 min-h-11 flex-1 rounded-full border px-4 text-[15px] focus-visible:ring-[3px] focus-visible:outline-none"
        />
        <button
          type="submit"
          data-autopilot="send"
          disabled={value.trim() === ''}
          className={cn(
            CHIP,
            'border-surf-red bg-surf-red focus-visible:ring-surf-red/40 shrink-0 text-white',
            'disabled:border-chat-hairline disabled:bg-chat-rail disabled:text-chat-ink-muted',
          )}
        >
          {chat.chat_send}
        </button>
      </form>
    );
  }

  if (prompt.kind === 'activity') {
    // The interactive card is in the thread; this is the control that arms it,
    // resets it and marks it.
    return <ActivityFooter prompt={prompt} attempt={attempt} onAnswer={onAnswer} onReveal={onReveal} />;
  }

  if (prompt.kind === 'gate') {
    // The video's own dialog carries Continue, so the footer's job is to get
    // the learner back to it. It used to be a line of text alone: a learner
    // who dismissed the player was left with no control at all and a card
    // that had already scrolled out of the thread.
    return (
      <div data-autopilot="answer" data-autopilot-kind="gate" className="flex flex-col gap-2">
        <Heading>{chat.chat_video_gate_hint}</Heading>
        <button
          type="button"
          onClick={onOpenGate}
          className={cn(
            CHIP,
            'border-surf-red bg-surf-red focus-visible:ring-surf-red/40 flex w-full items-center justify-center gap-2 rounded-xl text-white',
          )}
        >
          <Icon name="passport/play" aria-hidden className="size-4" />
          {chat.chat_video_watch}
        </button>
      </div>
    );
  }

  // `quick` — acknowledgements. Right-aligned, because they are the learner's
  // own voice, and the first option is the primary one.
  return (
    <div data-autopilot="answer" data-autopilot-kind="quick" className="flex flex-wrap justify-end gap-2">
      {prompt.options.map((option, index) => (
        <button
          key={option.id}
          type="button"
          data-autopilot="chip"
          style={{ '--chip-index': index } as CSSProperties}
          disabled={sending !== null}
          onClick={() => send(option.id, { kind: 'quick', optionId: option.id })}
          className={cn(
            CHIP,
            exitClass(option.id),
            index === 0
              ? 'border-surf-red bg-surf-red focus-visible:ring-surf-red/40 text-white'
              : 'border-chat-hairline bg-bubble-buddy text-chat-ink focus-visible:ring-surf-blue/40 hover:border-surf-blue',
          )}
        >
          {labelText(option.label)}
        </button>
      ))}
    </div>
  );
}
